import { Navigate, Route, Routes } from "react-router-dom";
import Person from "./routes/Person/index";
import PersonLayout from "./routes/Person/PersonLayout";
import PersonById from "./routes/Person/[id]";
import FirmaById from "./routes/Firma/[id]";
import Firma from "./routes/Firma/index";
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  HttpLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { graphqlApiUrl } from "./api/util";
import { getAuthToken } from "./lib/authToken";
import FirmaLayout from "./routes/Firma/FirmaLayout";
import KursusById from "./routes/Kursus/[id]";
import Kursus from "./routes/Kursus/index";
import LinjeById from "./routes/Linje/[id]";
import Linje from "./routes/Linje/index";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PagedPersonQueryProvider } from "./context/PagedPersonQueryProvider";
import { PagedKursusQueryProvider } from "./context/PagedKursusQueryProvider";
import { PagedLinjeQueryProvider } from "./context/PagedLinjeQueryProvider";
import { PagedFirmaQueryProvider } from "./context/PagedFirmaQueryProvider";
import KursusLinjeLayoutCommon from "./routes/Kursus/KursusLinjeLayoutCommon";
import AdminLayout from "./routes/Admin/AdminLayout";
import Mailskabeloner from "./components/pages/admin/id/Mailskabeloner";
import Dokumentskabeloner from "./components/pages/admin/id/Dokumentskabeloner";
import Kodelister from "./components/pages/admin/id/Kodelister";
import Brugere from "./components/pages/admin/id/Brugere";
import Jobs from "./components/pages/admin/id/Jobs";
import Rapporter from "./components/pages/admin/id/Rapporter";
import Dataudtraek from "./components/pages/admin/id/Dataudtraek";
import Prissaetning from "./components/pages/admin/id/Prissaetning";
import useTheme from "./hooks/useTheme";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "./components/ErrorFallback";
import { ConfirmContextProvider } from "./components/confirm/ConfirmContextProvider";
import { ConfirmDialog } from "./components/confirm/ConfirmDialog";
import { UserQueryProvider } from "./context/UserContext";
import { CustomProvider, CustomProviderProps } from "rsuite";
import { ReactNode, useEffect } from "react";
import { daDK } from "rsuite/esm/locales";
import { useAuth, RedirectToSignIn } from "@clerk/react";
import { setAuthTokenGetter } from "./lib/authToken";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Spinner from "./components/spinner/Spinner";
import Home from "./routes/Home";
import Efteruddannelse from "./routes/Efteruddannelse";
import ReviewThreadDetail from "./routes/Efteruddannelse/ReviewThreadDetail";
import EfteruddannelseLayout from "./routes/Efteruddannelse/EfteruddannelseLayout";

// For RSuite to work properly, we need to override the CustomProvider
// to return a ReactNode instead of an Element.
const RSuiteCustomProvider = CustomProvider as (
  props: CustomProviderProps,
) => ReactNode;

const apimKey = import.meta.env.OCP_APIM_SUBSCRIPTION_KEY as string | undefined;

const authLink = setContext(async (_, { headers }) => {
  const token = await getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(apimKey && { "Ocp-Apim-Subscription-Key": apimKey }),
    },
  };
});

const httpLink = new HttpLink({
  uri: graphqlApiUrl,
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "no-cache",
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      //  staleTime: Infinity
      staleTime: 0,
    },
  },
});

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route
        path="person"
        element={
          <ProtectedRoute>
            <PersonLayout />
          </ProtectedRoute>
        }
      >
        <Route path={"/person"} element={<Person />} />
        <Route path={"/person/:id"} element={<PersonById />} />
      </Route>
      <Route
        path="firma"
        element={
          <ProtectedRoute>
            <FirmaLayout />
          </ProtectedRoute>
        }
      >
        <Route path={"/firma"} element={<Firma />} />
        <Route path={"/firma/:id"} element={<FirmaById />} />
      </Route>
      <Route
        path="kursus"
        element={
          <ProtectedRoute>
            <KursusLinjeLayoutCommon />
          </ProtectedRoute>
        }
      >
        <Route path={"/kursus"} element={<Kursus />} />
        <Route path={"/kursus/:id"} element={<KursusById />} />
      </Route>
      <Route
        path="linje"
        element={
          <ProtectedRoute>
            <KursusLinjeLayoutCommon />
          </ProtectedRoute>
        }
      >
        <Route path={"/linje"} element={<Linje />} />
        <Route path={"/linje/:id"} element={<LinjeById />} />
      </Route>
      <Route
        path="efteruddannelse"
        element={
          <ProtectedRoute>
            <EfteruddannelseLayout />
          </ProtectedRoute>
        }
      >
        <Route path={"/efteruddannelse"} element={<Efteruddannelse />} />
        <Route
          path="/efteruddannelse/:subjectType/:ownerUserId/:subjectId"
          element={<ReviewThreadDetail />}
        />
      </Route>
      <Route
        path="admin"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path={"/admin/mailskabeloner"} element={<Mailskabeloner />} />
        <Route
          path={"/admin/dokumentskabeloner"}
          element={<Dokumentskabeloner />}
        />
        <Route path={"/admin/dataudtræk"} element={<Dataudtraek />} />
        <Route path={"/admin/prissætning"} element={<Prissaetning />} />
        <Route path={"/admin/jobs"} element={<Jobs />} />
        <Route path={"/admin/kodelister"} element={<Kodelister />} />
        <Route path={"/admin/rapporter"} element={<Rapporter />} />
        <Route path={"/admin/brugere"} element={<Brugere />} />
        <Route
          path={"/admin"}
          element={<Navigate replace to="/admin/mailskabeloner" />}
        />
      </Route>
    </Routes>
  );
}

function App() {
  useTheme();
  const { isLoaded, isSignedIn, getToken } = useAuth();

  useEffect(() => {
    if (!isLoaded) return;
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [isLoaded, getToken]);

  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner size={48} label="" inline={false} />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <ConfirmContextProvider>
            <UserQueryProvider>
              <PagedPersonQueryProvider>
                <PagedFirmaQueryProvider>
                  <PagedKursusQueryProvider>
                    <PagedLinjeQueryProvider>
                      <RSuiteCustomProvider locale={daDK}>
                        <AppRouter />
                        <ConfirmDialog />
                      </RSuiteCustomProvider>
                    </PagedLinjeQueryProvider>
                  </PagedKursusQueryProvider>
                </PagedFirmaQueryProvider>
              </PagedPersonQueryProvider>
            </UserQueryProvider>
          </ConfirmContextProvider>
        </QueryClientProvider>
      </ApolloProvider>
    </ErrorBoundary>
  );
}

export default App;
